import React, { useCallback, useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
import {
  ContentSection,
  StatusText,
  FooterMessage,
  ErrorMessage,
  SuccessMessage,
} from "../shared/Components";
import {
  Campaign,
  CampaignPrefilteringEndpoint,
  CampaignPrefilteringJob,
} from "../../appstore/models";
import {
  Box,
  Button,
  Container,
  ColumnLayout,
  FormField,
  Header,
  Link,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  TextFilter,
  Table,
  SelectProps,
} from "@awsui/components-react";
import styled from "styled-components";
import {
  createCampaignPrefilteringJob,
  listCampaignPrefilteringEndpoints,
  listCampaignPrefilteringJobs,
} from "../../common/api";
import appConfig from "../../common/appConfig";
import { humanizeDuration } from "../../common/helpers";
import { DynamoDB } from "aws-sdk";

const TableHeader = styled.div`
  justify-content: space-between;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 22px;
  &:first-child {
    font-weight: 700;
    font-size: 18px;
  }
`;

export interface NewCampaignPrefilteringJobProps {
  onCancel: () => void;
  onSuccess: () => void;
  campaign: Campaign;
  visible: boolean;
}

export const NewCampaignPrefilteringJobModal = (
  props: NewCampaignPrefilteringJobProps
) => {
  const { onCancel, onSuccess, campaign, visible } = props;
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [apiSuccess, setApiSuccess] = useState(false);

  const [
    selectedCampaignPrefilteringEndpoint,
    setSelectedCampaignPrefilteringEndpoint,
  ] = useState<SelectProps.Option | null>(null);
  const [campaignPrefilteringEndpoints, setCampaignPrefilteringEndpoints] =
    useState<CampaignPrefilteringEndpoint[]>([]);
  const fetchPrefilteringEndpoints = useCallback(async () => {
    const response = await listCampaignPrefilteringEndpoints();
    setCampaignPrefilteringEndpoints(response.campaignPrefilteringEndpoints);
  }, []);
  useEffect(() => {
    fetchPrefilteringEndpoints();
  }, [fetchPrefilteringEndpoints]);
  const create = useCallback(async () => {
    if (
      !selectedCampaignPrefilteringEndpoint ||
      !selectedCampaignPrefilteringEndpoint.value
    ) {
      return;
    }
    setCreating(true);
    setApiSuccess(false);
    setErrors([]);
    const createCampaignResult = await createCampaignPrefilteringJob({
      job_name: campaign.JobName,
      sm_detect_features_endpoint: selectedCampaignPrefilteringEndpoint.value,
    });
    if (!createCampaignResult.success) {
      setErrors(createCampaignResult.errors);
      setCreating(false);
      setApiSuccess(false);
      return;
    }
    setCreating(false);
    setApiSuccess(true);
    onSuccess();
  }, [selectedCampaignPrefilteringEndpoint, campaign.JobName, onSuccess]);
  return (
    <Modal
      visible={visible}
      onDismiss={() => onCancel()}
      header="New Campaign Prefiltering Job"
      closeAriaLabel="Close dialog"
      footer={
        <div className="awsui-util-f-r">
          {errors ? (
            <FooterMessage>
              {errors.map((message, i) => (
                <ErrorMessage key={i} message={message} />
              ))}
            </FooterMessage>
          ) : null}
          {apiSuccess ? (
            <FooterMessage>
              <SuccessMessage>Success</SuccessMessage>
            </FooterMessage>
          ) : null}
          <Button variant="link" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={selectedCampaignPrefilteringEndpoint === null || creating}
            onClick={() => create()}
          >
            {creating
              ? "Starting Prefiltering Job..."
              : "Start Prefiltering Job"}
          </Button>
        </div>
      }
    >
      <ColumnLayout borders="horizontal">
        <SpaceBetween size="l">
          <FormField label="Prefiltering Model Endpoint">
            <Select
              selectedOption={selectedCampaignPrefilteringEndpoint}
              onChange={({ detail }) =>
                setSelectedCampaignPrefilteringEndpoint(detail.selectedOption)
              }
              options={[
                {
                  label: "Random",
                  value: "Random",
                },
                ...campaignPrefilteringEndpoints.map(
                  (campaignPrefilteringEndpoint) => ({
                    label:
                      campaignPrefilteringEndpoint.CampaignPrefilteringEndpointName,
                    value: campaignPrefilteringEndpoint.EndpointName,
                  })
                ),
              ]}
              selectedAriaLabel="Selected"
              placeholder="None"
            />
          </FormField>
        </SpaceBetween>
      </ColumnLayout>
    </Modal>
  );
};

const CampaignPrefilteringJobsTable = (props: { campaign: Campaign }) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const { campaign } = props;
  const pageSize = 8;
  const [campaignPrefilteringJobs, setCampaignPrefilteringJobs] = useState<
    CampaignPrefilteringJob[]
  >([]);
  const [loadingError, setLoadingError] = useState<undefined | string>(
    undefined
  );
  const [showNewPrefilteringJob, setShowNewPrefilteringJob] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<
    DynamoDB.Key | undefined
  >(undefined);

  const fetchPage = (pageNumber: number) => {
    if (pageNumber === currentPageIndex) {
      return;
    }

    setCurrentPageIndex(pageNumber);
    if (pageNumber === pageCount && hasNextPage) {
      setSearchTriggered(true);
    }
  };
  const refresh = () => {
    setCurrentPageIndex(1);
    setCampaignPrefilteringJobs([]);
    setHasNextPage(true);
    setPageCount(1);
    setLastEvaluatedKey(undefined);
    setSearchTriggered(true);
  };

  const loadCampaignPrefilteringJobs = useCallback(async () => {
    if (!hasNextPage) {
      return;
    }
    setLoadingError(undefined);
    try {
      const listCampaignPrefilteringJobsResult =
        await listCampaignPrefilteringJobs(campaign.JobName, pageSize, {
          LastEvaluatedKey: lastEvaluatedKey,
        });
      setLastEvaluatedKey(listCampaignPrefilteringJobsResult.LastEvaluatedKey);
      setPageCount(
        listCampaignPrefilteringJobsResult.LastEvaluatedKey
          ? pageCount + 1
          : pageCount
      );
      setCampaignPrefilteringJobs([
        ...campaignPrefilteringJobs,
        ...listCampaignPrefilteringJobsResult.campaignPrefilteringJobs,
      ]);
      if (listCampaignPrefilteringJobsResult.LastEvaluatedKey) {
        setHasNextPage(true);
      } else {
        setHasNextPage(false);
      }
    } catch (e) {
      console.log("Error loading prefiltering results", e);
      setLoadingError("Error loading prefiltering results");
    }
  }, [
    campaign,
    campaignPrefilteringJobs,
    pageCount,
    hasNextPage,
    lastEvaluatedKey,
  ]);

  useEffect(() => {
    if (searchTriggered) {
      loadCampaignPrefilteringJobs();
      setSearchTriggered(false);
    }
  }, [searchTriggered, loadCampaignPrefilteringJobs]);

  useEffect(() => {
    setSearchTriggered(true);
  }, []);

  if (loadingError) {
    return (
      <ContentSection>
        <Container header={<Header variant="h2">Prefiltering Summary</Header>}>
          <div>{loadingError}</div>
        </Container>
      </ContentSection>
    );
  }

  return (
    <ContentSection>
      <Table
        trackBy="CampaignPrefilteringId"
        columnDefinitions={[
          {
            id: "Status",
            header: "Status",
            cell: (e) => {
              const statusCode =
                e.Status === "Complete"
                  ? "success"
                  : e.Status === "Failed"
                  ? "error"
                  : "running";
              return (
                <StatusText key={e.CampaignPrefilteringId} status={statusCode}>
                  {e.Status}
                </StatusText>
              );
            },
            sortingField: "Status",
          },
          {
            id: "CampaignPrefilteringId",
            header: "Campaign Prefiltering Id",
            cell: (e) => {
              if (e.CreatePrefilteringJobStepFunctionExecutionArn) {
                const createCampaignPrefilteringtepFunctionExecutionConsoleUrl =
                  new URL(
                    `https://${appConfig.consoleHostname}/states/home?region=${appConfig.awsRegion}#/executions/details/${e.CreatePrefilteringJobStepFunctionExecutionArn}`
                  );
                return (
                  <Link
                    href={
                      createCampaignPrefilteringtepFunctionExecutionConsoleUrl.href
                    }
                    target="_blank"
                  >
                    {e.CampaignPrefilteringId}
                  </Link>
                );
              }
              return e.CampaignPrefilteringId;
            },
            sortingField: "CampaignPrefilteringId",
          },
          {
            id: "DateCreated",
            header: "Date Created",
            cell: (e) => e.DateCreated,
            sortingField: "DateCreated",
          },
          {
            id: "DateCompleted",
            header: "Date Completed",
            cell: (e) => e.DateCompleted,
            sortingField: "DateCompleted",
          },
          {
            id: "Duration",
            header: "Duration",
            cell: (e) => {
              if (!e.DateCompleted) {
                const durationMs =
                  new Date().getTime() -
                  new Date(`${e.DateCreated}Z`).getTime();
                return humanizeDuration(durationMs);
              }
              return humanizeDuration(e.DurationSeconds * 1000);
            },
            sortingField: "Duration",
          },
          {
            id: "EndpointName",
            header: "Endpoint Name",
            cell: (e) => {
              const endpointUrl = `https://${appConfig.consoleHostname}/sagemaker/home?region=${appConfig.awsRegion}#/endpoints/${e.EndpointName}`;
              return (
                <Link
                  key={e.CampaignPrefilteringId}
                  href={endpointUrl}
                  target="_blank"
                >
                  {e.EndpointName}
                </Link>
              );
            },
            sortingField: "EndpointName",
          },
          {
            id: "ErrorChipCount",
            header: "Error Chip Count",
            cell: (e) => e.ErrorChipCount,
            sortingField: "ErrorChipCount",
          },
          {
            id: "ErrorMessage",
            header: "Error Message",
            cell: (e) => e.ErrorMessage,
            sortingField: "ErrorMessage",
          },
        ]}
        items={campaignPrefilteringJobs.slice(
          (currentPageIndex - 1) * pageSize,
          currentPageIndex * pageSize
        )}
        loadingText="Loading jobs"
        visibleColumns={[
          "Status",
          "CampaignPrefilteringId",
          "DateCreated",
          "DateCompleted",
          "Duration",
          "EndpointName",
          "ErrorChipCount",
          "ErrorMessage",
        ]}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No jobs</b>
            <Box padding={{ bottom: "s" }} variant="p" color="inherit">
              No jobs to display.
            </Box>
          </Box>
        }
        filter={
          <TextFilter filteringText="" filteringPlaceholder="Find jobs" />
        }
        header={
          <TableHeader>
            Prefiltering Jobs
            <Button
              variant="primary"
              onClick={() => setShowNewPrefilteringJob(true)}
            >
              New Prefiltering Job
            </Button>
          </TableHeader>
        }
        pagination={
          <Pagination
            openEnd={hasNextPage}
            currentPageIndex={currentPageIndex}
            onChange={({ detail }) => fetchPage(detail.currentPageIndex)}
            pagesCount={pageCount}
            ariaLabels={{
              nextPageLabel: "Next page",
              previousPageLabel: "Previous page",
              pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
            }}
          />
        }
      />
      <NewCampaignPrefilteringJobModal
        campaign={campaign}
        onSuccess={() => {
          setShowNewPrefilteringJob(false);
          refresh();
        }}
        onCancel={() => setShowNewPrefilteringJob(false)}
        visible={showNewPrefilteringJob}
      />
    </ContentSection>
  );
};

export default CampaignPrefilteringJobsTable;
